import React, { useEffect, useRef, useState } from 'react';
import {
  getViewportIcon,
  displayViewportName,
  isSingleLinkMode,
  rangeLabel,
  availableBreakpoints,
  addBreakpoint,
  getInheritingFrom,
  validateViewports,
  isWrongUrlType,
} from '../../utils/viewportUtils';
import { figmaFileName } from '../../utils/figmaPaste';
import useFigmaLinkRows from '../../utils/useFigmaLinkRows';
import FigmaLinkNote from './FigmaLinkNote';
import ShareGuideModal from './ShareGuideModal';
import AeroFigmaLinkNote from '../Aero/AeroFigmaLinkNote';
import AeroShareGuideModal from '../Aero/AeroShareGuideModal';
import './ViewportEditor.css';

/**
 * Reusable viewport editor: the per-breakpoint Figma link rows plus inline
 * validation, lifted verbatim from FormContent so Figmafolio and Aero share
 * one implementation. The component boundary is intentionally generic:
 *
 *   - viewports          the array of { name, minWidth, figmaUrl }
 *   - onViewportsChange   called with the next array on every edit
 *   - variant             'default' (Figmafolio) | 'aero' (re-skinned)
 *   - linkCheck           Figmafolio only. Turns design / file / branch links into
 *                         prototype links on paste, asks the server whether anyone can
 *                         open the prototype (one site at a time, from this page only),
 *                         and shows the result under the field. Never blocks saving.
 *
 * It owns no save/preview action: those are caller concerns. Editing rules are
 * unchanged: 2 to 6 viewports, a locked 0px base row, editable name and width,
 * empty links inherit from the next larger viewport (the row says "Same as Desktop"; clear the
 * field to go back to that). Every width chip is a visible, editable box. Rows run smallest first (Phone on top). A new site starts as one row, "All screens";
 * "Add breakpoint" opens a short menu (Phone, Tablet, Large desktop, custom width).
 */
export default function ViewportEditor({ viewports = [], onViewportsChange, variant = 'default', linkCheck = false }) {
  const [editingName, setEditingName] = useState(null);
  const [editingWidth, setEditingWidth] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [phoneShown, setPhoneShown] = useState(false); // left the one-row start
  const [menuOpen, setMenuOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const menuRef = useRef(null);

  // The one-row start: the two default rows, no phone link. "All screens" is the Desktop row; an
  // empty Phone row borrows it, so one link serves every screen size.
  // Once it has shown more than one row it stays that way: clearing the Phone link must not make the
  // row disappear under your hands.
  const leftSingle = useRef(false);
  const singleNow = isSingleLinkMode(viewports);
  if (!singleNow) leftSingle.current = true;
  const single = !phoneShown && !leftSingle.current && singleNow;

  // Smallest first (Phone on top), as it has always been here. The row the others borrow from is
  // the biggest one, further down.
  const sortedViewports = [...viewports].sort((a, b) => a.minWidth - b.minWidth);
  const shownViewports = single ? sortedViewports.filter((v) => v.minWidth === 768) : sortedViewports;

  // The Aero variant uses the aero-input field styling (loaded with the Aero
  // theme); Figmafolio keeps its form-input class. Everything else is shared.
  const urlInputBase = variant === 'aero' ? 'aero-input' : 'form-input';

  // ---- link check (both brands): one hook, see utils/useFigmaLinkRows.js ----
  const setRowUrl = (minWidth, newUrl) => {
    onViewportsChange(viewports.map((v) => (v.minWidth === minWidth ? { ...v, figmaUrl: newUrl || null } : v)));
  };
  const linkRows = useFigmaLinkRows({
    rows: viewports.map((v) => ({ id: v.minWidth, url: v.figmaUrl })),
    setUrl: setRowUrl,
    enabled: linkCheck,
  });
  const Note = variant === 'aero' ? AeroFigmaLinkNote : FigmaLinkNote;
  const Guide = variant === 'aero' ? AeroShareGuideModal : ShareGuideModal;

  // close the Add breakpoint menu on an outside click or Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleViewportUrlChange = (minWidth, newUrl) => {
    const updated = viewports.map((v) =>
      v.minWidth === minWidth ? { ...v, figmaUrl: newUrl || null } : v
    );
    onViewportsChange(updated);
  };

  const handleViewportNameChange = (minWidth, newName) => {
    const updated = viewports.map((v) =>
      v.minWidth === minWidth ? { ...v, name: newName } : v
    );
    onViewportsChange(updated);
  };

  const handleViewportWidthChange = (oldMinWidth, newMinWidth) => {
    const parsedWidth = parseInt(newMinWidth, 10);
    if (isNaN(parsedWidth) || parsedWidth < 1 || parsedWidth > 3840) {
      setNotice('Enter a width between 1 and 3840');
      return;
    }
    if (parsedWidth === oldMinWidth) return;

    // A width can only be used once
    if (viewports.some((v) => v.minWidth !== oldMinWidth && v.minWidth === parsedWidth)) {
      setNotice(`A breakpoint already starts at ${parsedWidth}px`);
      return;
    }

    setNotice('');
    // The rows sort themselves by width, so a row made bigger than the next one simply moves.
    onViewportsChange(viewports.map((v) => (v.minWidth === oldMinWidth ? { ...v, minWidth: parsedWidth } : v)));
  };

  const handleChoose = (id, width) => {
    setMenuOpen(false);
    setCustomOpen(false);
    setNotice('');
    if (id === 'phone') {
      setPhoneShown(true);
      return;
    }
    const result = addBreakpoint(viewports, id, width);
    if (result.error) {
      setNotice(result.error);
      return;
    }
    setPhoneShown(true);
    onViewportsChange(result.viewports);
  };

  const handleDeleteViewport = (minWidth) => {
    if (viewports.length <= 2) return;
    if (minWidth === 0) return; // Can't delete base viewport

    const updated = viewports.filter((v) => v.minWidth !== minWidth);
    onViewportsChange(updated);
    setDeleteConfirmId(null);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setEditingName(null);
    }
  };

  // The smallest row always starts at 0px, so its chip edits where it ends: "up to 767px" moves
  // the start of the row above it. Every other chip edits its own start.
  const commitWidth = (viewport, raw) => {
    if (viewport.minWidth !== 0) {
      handleViewportWidthChange(viewport.minWidth, raw);
      return;
    }
    const above = [...viewports].filter((v) => v.minWidth > 0).sort((a, b) => a.minWidth - b.minWidth)[0];
    const end = parseInt(raw, 10);
    if (!above || isNaN(end)) return;
    handleViewportWidthChange(above.minWidth, end + 1);
  };

  const widthInputValue = (viewport) => {
    if (viewport.minWidth !== 0) return viewport.minWidth;
    const above = [...viewports].filter((v) => v.minWidth > 0).sort((a, b) => a.minWidth - b.minWidth)[0];
    return above ? above.minWidth - 1 : 767;
  };

  const handleWidthKeyDown = (e, viewport) => {
    if (e.key === 'Enter') {
      commitWidth(viewport, e.target.value);
      setEditingWidth(null);
    } else if (e.key === 'Escape') {
      setEditingWidth(null);
    }
  };

  const handleWidthBlur = (e, viewport) => {
    commitWidth(viewport, e.target.value);
    setEditingWidth(null);
  };

  // Check for validation errors
  const validationErrors = validateViewports(viewports);
  const hasUrlError = !linkCheck && viewports.some((v) => isWrongUrlType(v.figmaUrl));

  const menuItems = [
    ...(single ? [{ id: 'phone', name: 'Phone', hint: 'up to 767px' }] : []),
    ...availableBreakpoints(viewports),
  ];

  return (
    <div className={`viewport-editor${variant === 'aero' ? ' viewport-editor-aero' : ''}`}>
      <div className="viewport-list">
        {shownViewports.map((viewport) => {
          const inheritingFrom = getInheritingFrom(viewport, viewports);
          const isBaseViewport = viewport.minWidth === 0;
          const label = single ? 'All screens' : displayViewportName(viewport.name);

          return (
            <div key={viewport.minWidth} className="viewport-row">
              <div className="viewport-info">
                <span className="viewport-icon">{single ? '\u{1F5A5}\uFE0F' : getViewportIcon(viewport.minWidth)}</span>

                {single ? (
                  <span className="viewport-name-static">{label}</span>
                ) : editingName === viewport.minWidth ? (
                  <input
                    type="text"
                    className="viewport-name-input"
                    value={viewport.name}
                    onChange={(e) => handleViewportNameChange(viewport.minWidth, e.target.value)}
                    onKeyDown={(e) => handleNameKeyDown(e, viewport.minWidth)}
                    onBlur={() => setEditingName(null)}
                    autoFocus
                  />
                ) : (
                  <button
                    className="viewport-name-btn"
                    onClick={() => setEditingName(viewport.minWidth)}
                    type="button"
                  >
                    {label}
                  </button>
                )}

                {single ? (
                  <span className="viewport-width-static">Every screen size</span>
                ) : editingWidth === viewport.minWidth ? (
                  <span className="viewport-width-edit">
                    {isBaseViewport && <span className="viewport-width-edit-label">up to</span>}
                    <input
                      type="number"
                      className="viewport-width-input"
                      defaultValue={widthInputValue(viewport)}
                      aria-label={isBaseViewport ? 'Where this breakpoint ends, in pixels' : 'Where this breakpoint starts, in pixels'}
                      onKeyDown={(e) => handleWidthKeyDown(e, viewport)}
                      onBlur={(e) => handleWidthBlur(e, viewport)}
                      min="1"
                      max="3840"
                      autoFocus
                    />
                    <span className="viewport-width-edit-label">px</span>
                  </span>
                ) : (
                  <button
                    className="viewport-width-btn"
                    onClick={() => setEditingWidth(viewport.minWidth)}
                    type="button"
                    title={isBaseViewport ? 'Click to change where this ends' : 'Click to change where this starts'}
                  >
                    {rangeLabel(viewport, viewports)}
                    <svg className="viewport-width-pencil" width="1em" height="1em" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M11 2.5l2.5 2.5L5.5 13H3v-2.5L11 2.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="viewport-url-container">
                <input
                  className={`${urlInputBase} viewport-url-input ${!linkCheck && isWrongUrlType(viewport.figmaUrl) ? 'error' : ''}`}
                  type="text"
                  aria-label={`${label} Figma link`}
                  placeholder={!single && inheritingFrom ? `Same as ${displayViewportName(inheritingFrom)}` : 'https://www.figma.com/proto/...'}
                  value={viewport.figmaUrl || ''}
                  onChange={(e) => handleViewportUrlChange(viewport.minWidth, e.target.value)}
                  onPaste={linkCheck ? (e) => linkRows.handlePaste(viewport.minWidth, e) : undefined}
                  onBlur={linkCheck ? (e) => linkRows.handleBlur(viewport.minWidth, e.target.value) : undefined}
                />
                {linkCheck && (
                  <Note
                    kind={linkRows.noteKind(viewport.minWidth, viewport.figmaUrl)}
                    onShowHow={() => linkRows.guide.open(viewport.minWidth)}
                    onRecheck={() => linkRows.recheck(viewport.minWidth, viewport.figmaUrl)}
                  />
                )}
                {!linkCheck && isWrongUrlType(viewport.figmaUrl) && (
                  <p className="figma-url-warning viewport-url-warning">
                    ⚠️ Use a prototype link (figma.com/proto/...)
                  </p>
                )}
                {!single && isBaseViewport && !viewport.figmaUrl && (
                  <p className="viewport-row-help">Copy the link to your phone flow's starting frame in Figma.</p>
                )}
              </div>

              <div className="viewport-actions">
                {!single && viewports.length > 2 && !isBaseViewport && (
                  <>
                    {deleteConfirmId === viewport.minWidth ? (
                      <div className="viewport-delete-confirm">
                        <span>Delete?</span>
                        <button
                          className="viewport-delete-confirm-btn yes"
                          onClick={() => handleDeleteViewport(viewport.minWidth)}
                          type="button"
                        >
                          Yes
                        </button>
                        <button
                          className="viewport-delete-confirm-btn no"
                          onClick={() => setDeleteConfirmId(null)}
                          type="button"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        className="viewport-delete-btn"
                        onClick={() => setDeleteConfirmId(viewport.minWidth)}
                        type="button"
                        title="Delete breakpoint"
                        aria-label={`Delete ${label}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}

        {menuItems.length > 0 && (
          <div className="viewport-add" ref={menuRef}>
            <button
              className="viewport-add-btn"
              onClick={() => setMenuOpen((o) => !o)}
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
                <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Add breakpoint
            </button>
            {menuOpen && (
              <div className="viewport-menu" role="menu">
                {menuItems.map((item) =>
                  item.id === 'custom' ? (
                    customOpen ? (
                      <form
                        key={item.id}
                        className="viewport-menu-custom"
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleChoose('custom', e.target.elements.width.value);
                        }}
                      >
                        <input name="width" type="number" min="1" max="3840" placeholder="Width in px" aria-label="Width in pixels" autoFocus />
                        <button type="submit">Add</button>
                      </form>
                    ) : (
                      <button key={item.id} type="button" role="menuitem" className="viewport-menu-item" onClick={() => setCustomOpen(true)}>
                        <span>{item.name}</span>
                      </button>
                    )
                  ) : (
                    <button key={item.id} type="button" role="menuitem" className="viewport-menu-item" onClick={() => handleChoose(item.id)}>
                      <span>{item.name}</span>
                      <span className="viewport-menu-hint">{item.hint}</span>
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {notice && <p className="viewport-validation-error" role="alert">{notice}</p>}

      {linkCheck && (
        <Guide
          show={linkRows.guide.openFor !== null}
          onClose={linkRows.guide.close}
          recheck={linkRows.guide.recheckQuiet}
          fileName={figmaFileName((viewports.find((v) => v.minWidth === linkRows.guide.openFor) || {}).figmaUrl)}
          onShared={() => {}}
        />
      )}

      {validationErrors.length > 0 && !hasUrlError && (
        <div className="viewport-validation-errors">
          {validationErrors.map((error, idx) => (
            <p key={idx} className="viewport-validation-error">⚠️ {error}</p>
          ))}
        </div>
      )}
    </div>
  );
}
